import { useState } from "react";
import { Link } from "react-router";
import API from "@app/services/api";
import useUser from "@app/zustand/user";

export default function CguAcceptanceModal() {
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setUser = useUser((state) => state.setUser);

  async function handleAccept() {
    setLoading(true);
    setError(null);
    const res = await API.post({ path: "/user/accept-cgu" });
    if (res?.ok && res?.data?.user) {
      setUser(res.data.user);
    } else {
      setError("Une erreur est survenue. Veuillez réessayer.");
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="mx-4 flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-primary/90 px-8 py-6 text-white">
          <h1 className="text-xl font-bold">Conditions d'utilisation</h1>
          <p className="mt-1 text-sm text-white/80">
            Avant d'accéder au tableau de bord, veuillez lire et accepter nos
            conditions.
          </p>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          <p className="text-sm text-slate-700">
            MAGE-X est une plateforme réservée aux professionnels de santé
            dûment certifiés. En utilisant cette plateforme, vous vous engagez à
            respecter l'éthique professionnelle, le secret médical et les lois
            en vigueur relatives à la protection des données de santé.
          </p>

          <ul className="mt-4 space-y-2 text-sm text-slate-600">
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-primary">•</span>
              Les données patients sont traitées de manière confidentielle et
              sécurisée.
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-primary">•</span>
              Vous êtes responsable des accès effectués depuis votre compte.
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-primary">•</span>
              Toute utilisation abusive entraînera la suspension immédiate du
              compte.
            </li>
          </ul>

          <div className="mt-6 flex gap-4 text-sm">
            <Link
              to="/cgus"
              target="_blank"
              className="font-medium text-primary underline underline-offset-2"
            >
              Lire les CGU complètes
            </Link>
            <Link
              to="/confidentialité"
              target="_blank"
              className="font-medium text-primary underline underline-offset-2"
            >
              Politique de confidentialité
            </Link>
          </div>

          {/* Checkbox */}
          <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 p-4 hover:border-primary/50 hover:bg-primary/5">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-primary"
            />
            <span className="text-sm text-slate-700">
              J'ai lu et j'accepte les{" "}
              <Link
                to="/cgus"
                target="_blank"
                className="font-medium text-primary"
                onClick={(e) => e.stopPropagation()}
              >
                Conditions Générales d'Utilisation
              </Link>{" "}
              et la{" "}
              <Link
                to="/confidentialité"
                target="_blank"
                className="font-medium text-primary"
                onClick={(e) => e.stopPropagation()}
              >
                Politique de confidentialité
              </Link>
              .
            </span>
          </label>

          {error && (
            <p className="mt-3 text-sm text-red-600">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-100 px-8 py-5">
          <button
            onClick={handleAccept}
            disabled={!accepted || loading}
            className="w-full rounded-lg bg-gradient-to-r from-primary to-primary/90 py-2.5 text-sm font-semibold text-white disabled:pointer-events-none disabled:opacity-40"
          >
            {loading ? "Enregistrement..." : "Accéder au tableau de bord"}
          </button>
        </div>
      </div>
    </div>
  );
}
